"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import posthog from "posthog-js";
import {
  FEATURE_FLAG_DEFAULTS,
  getDefaults,
  type FeatureFlagName,
  type FeatureFlagValues,
} from "@/lib/feature-flags";

const FeatureFlagContext = createContext<FeatureFlagValues>(getDefaults());

/**
 * Provides feature flags to the React tree.
 *
 * On mount, merges hardcoded defaults with PostHog overrides.
 * Falls back to defaults if PostHog is unavailable.
 */
export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlagValues>(getDefaults);

  useEffect(() => {
    // PostHog may not be initialized (missing key, dev mode, etc.)
    if (typeof posthog?.isFeatureEnabled !== "function") return;

    function loadFlags() {
      const merged = getDefaults();
      for (const key of Object.keys(FEATURE_FLAG_DEFAULTS) as FeatureFlagName[]) {
        const override = posthog.isFeatureEnabled(key);
        if (typeof override === "boolean") {
          merged[key] = override;
        }
      }
      setFlags(merged);
    }

    // Load immediately if PostHog already has flags
    loadFlags();

    // Re-load when PostHog fetches fresh flags (e.g. after identify)
    posthog.onFeatureFlags(loadFlags);
  }, []);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/** Read a single feature flag. Returns the PostHog override or the hardcoded default. */
export function useFeatureFlag(flag: FeatureFlagName): boolean {
  const flags = useContext(FeatureFlagContext);
  return flags[flag];
}

/** Read all feature flags at once. */
export function useFeatureFlags(): FeatureFlagValues {
  return useContext(FeatureFlagContext);
}
